import { ApolloError, ApolloQueryResult, useQuery } from '@apollo/client';
import { useContext, useEffect, useMemo, useState } from 'react';

import { isDemoEnvironment } from '../../../constants/global';
import { GhostModeContext } from '../../../context/GhostMode/GhostMode';
import { InitialMockData, KeysMockData } from '../../../DevEnvironment/initialMockData';
import useDevEnvironmentData from '../../../DevEnvironment/useDevEnvironmentData';
import { handleGql403 } from '../../../utils';
import { GetUserByOrganizationQuery, GetUserByOrganizationQueryVariables, User } from '../types';
import { GET_USER_BY_ORGANIZATION } from './getUser';

type Data = Pick<User, 'assignments' | 'email' | 'firstName' | 'lastName' | 'id' | 'phone' | 'roleList' | 'status'>;

export type UserResponse = {
  data?: Data;
  loading: boolean;
  error?: ApolloError;
  refetch: () => Promise<ApolloQueryResult<GetUserByOrganizationQuery>>;
};

const useFetchUserByOrganization = (id?: string, shouldSkipInGhostMode = true): UserResponse => {
  const [result, setResult] = useState<Data>();
  const { getItem } = useDevEnvironmentData();
  const { isGhostMode } = useContext(GhostModeContext);
  const { data, loading, error, refetch } = useQuery<GetUserByOrganizationQuery, GetUserByOrganizationQueryVariables>(
    GET_USER_BY_ORGANIZATION,
    {
      variables: {
        id: String(id),
      },
      skip: !id || (shouldSkipInGhostMode && isGhostMode) || isDemoEnvironment,
      onError: handleGql403,
    }
  );

  const memoizedUserData = useMemo(() => data?.loggedInUserByOrganization, [data?.loggedInUserByOrganization]);

  const refetchData = (): Promise<ApolloQueryResult<GetUserByOrganizationQuery>> =>
    getItem('userByOrganization' as KeysMockData, InitialMockData.userByOrganization.id as never).then(
      (updatedData) => {
        setResult(updatedData);
      }
    ) as Promise<ApolloQueryResult<GetUserByOrganizationQuery>>;

  useEffect(() => {
    if (isDemoEnvironment) {
      refetchData();
    }
  }, []);

  return {
    data: isDemoEnvironment ? result : memoizedUserData,
    loading,
    error,
    refetch: isDemoEnvironment ? refetchData : refetch,
  };
};

export default useFetchUserByOrganization;
